import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, Trash2, Pause, Play, Download } from 'lucide-react';

export default function WsPlaygroundPage() {
  const [events, setEvents] = useState([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState('');
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  useEffect(() => {
    const handler = (e) => {
      if (pausedRef.current) return;
      const detail = e.detail || {};
      setEvents(prev => [{ ...detail, _t: Date.now() }, ...prev].slice(0, 200));
    };
    window.addEventListener('ws:dashboard', handler);
    return () => window.removeEventListener('ws:dashboard', handler);
  }, []);

  const filtered = filter
    ? events.filter(e => JSON.stringify(e).toLowerCase().includes(filter.toLowerCase()))
    : events;

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(events, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ws-events-${Date.now()}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="ws-playground-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
            <Activity size={20} className="text-cyan-700 dark:text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-outfit font-bold">WebSocket Playground</h1>
            <p className="text-xs text-muted-foreground">Live admin diagnostic for /api/ws/dashboard event stream.</p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-sm font-outfit">Live event log · {events.length} captured</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by type or value…"
                className="h-8 px-2 text-xs rounded border border-border bg-background w-48"
                data-testid="ws-filter"
              />
              <Button size="sm" variant="outline" onClick={() => setPaused(p => !p)} data-testid="ws-pause">
                {paused ? <><Play size={12} className="mr-1" />Resume</> : <><Pause size={12} className="mr-1" />Pause</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEvents([])} data-testid="ws-clear">
                <Trash2 size={12} className="mr-1" />Clear
              </Button>
              <Button size="sm" variant="outline" onClick={exportJson} disabled={events.length === 0} data-testid="ws-export">
                <Download size={12} className="mr-1" />JSON
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground" data-testid="ws-empty">
                {paused ? 'Paused. Click Resume to start capturing.' : 'No events yet. Trigger an action (e.g., create an expense) to see frames stream in.'}
              </div>
            ) : (
              <ul className="divide-y divide-border max-h-[600px] overflow-y-auto" data-testid="ws-event-list">
                {filtered.map((e, i) => (
                  <li key={`${e._t}-${i}`} className="px-4 py-2 text-xs font-mono">
                    <div className="flex items-center justify-between mb-0.5 gap-2">
                      <span className="font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-400">{e.type}</span>
                      <span className="text-muted-foreground">{new Date(e._t).toLocaleTimeString()}</span>
                    </div>
                    <pre className="text-[10px] text-foreground whitespace-pre-wrap break-all bg-muted/40 rounded px-2 py-1 overflow-x-auto">
{JSON.stringify({ ...e, _t: undefined }, null, 2)}
                    </pre>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
